import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { writeFileXLSX, utils } from "xlsx";
import { BasicTable, Button, ButtonLink, ModalDefault, Notice, Text } from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import { isValidCPF, isValidCNPJ } from "../../../../helpers";
import { EMPLOYEE_SHEET_COLUMNS, normalizeEmployeeSheetRow, employeeSheetLabel } from "../../../../helpers/employeeSheet";
import {
  ImportCounts,
  ImportResultTable,
  PreviewStatusCell,
  readSheetFile,
  SheetDropZone,
  TableScroll,
} from "../../../../components/SheetImport";
import { escapeHtml } from "@components/ow";

const REQUIRED_COLS = EMPLOYEE_SHEET_COLUMNS.filter((c) => c.required).map((c) => c.key);
const OPTIONAL_COLS = EMPLOYEE_SHEET_COLUMNS.filter((c) => !c.required).map((c) => c.key);
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_ROWS = 50;

const STEP_UPLOAD = "upload";
const STEP_PREVIEW = "preview";
const STEP_RESULT = "result";

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Inline = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const labels = (keys) => keys.map(employeeSheetLabel).join(", ");

function downloadTemplate() {
  const templateData = [
    {
      name: "Maria Silva",
      email: "maria@example.com",
      document: "11.222.333/0001-81",
      phone: "11999999999",
      job: "Desenvolvedora",
      corporate_email: "maria.silva@example.com",
      erp_code: "ERP-001",
    },
    {
      name: "João Santos",
      email: "joao@example.com",
      document: "123.456.789-09",
      phone: "11988888888",
      job: "Designer",
      corporate_email: "",
      erp_code: "",
    },
  ];
  const ws = utils.json_to_sheet(templateData.map((row) => Object.fromEntries(EMPLOYEE_SHEET_COLUMNS.map((c) => [c.label, row[c.key]]))));
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, "Colaboradores");
  writeFileXLSX(wb, "modelo_importacao_colaboradores.xlsx");
}

// Mesmas regras da importação antiga
function validateRows(data) {
  const errors = {};
  data.forEach((item, i) => {
    const rowErrs = [];
    if (!item.name || !item.name.toString().trim()) {
      rowErrs.push("Nome é obrigatório");
    }
    if (!item.email || !EMAIL_REGEX.test(item.email.toString().trim())) {
      rowErrs.push("Email inválido ou ausente");
    }
    const docDigits = (item.document || "").toString().replace(/\D/g, "");
    if (docDigits.length !== 11 && docDigits.length !== 14) {
      rowErrs.push("CPF/CNPJ inválido (deve ter 11 ou 14 dígitos)");
    } else if (docDigits.length === 11 && !isValidCPF(docDigits)) {
      rowErrs.push("CPF inválido");
    } else if (docDigits.length === 14 && !isValidCNPJ(docDigits)) {
      rowErrs.push("CNPJ inválido");
    }
    if (item.phone !== undefined && item.phone !== null && item.phone !== "") {
      const phoneDigits = (item.phone || "").toString().replace(/\D/g, "");
      if (phoneDigits.length < 10 || phoneDigits.length > 11) {
        rowErrs.push("WhatsApp inválido");
      }
    }
    if (item.corporate_email !== undefined && item.corporate_email !== null && item.corporate_email !== "") {
      if (!EMAIL_REGEX.test(item.corporate_email.toString().trim())) {
        rowErrs.push("Email corporativo inválido");
      }
    }
    if (item.erp_code !== undefined && item.erp_code !== null && item.erp_code !== "") {
      if (item.erp_code.toString().trim().length > 100) {
        rowErrs.push("Código ERP excede 100 caracteres");
      }
    }
    if (rowErrs.length > 0) {
      errors[i] = rowErrs;
    }
  });
  return errors;
}

const cellText = (value) => (value === undefined || value === null || value === "" ? "-" : String(value));

const PREVIEW_COLUMNS = [
  { id: "index", header: "#", size: 50, cell: ({ row }) => row.index + 1 },
  {
    id: "employee",
    header: "Colaborador",
    size: 220,
    cell: ({ row }) => (
      <Stack>
        <Text $text={escapeHtml(cellText(row.original.name))} $variant="paragraph_small_bold" $color="N900" />
        <Text $text={escapeHtml(row.original.email ? String(row.original.email) : "")} $variant="caption_medium" $color="N400" />
      </Stack>
    ),
  },
  { id: "document", header: "CPF/CNPJ", size: 150, cell: ({ row }) => cellText(row.original.document) },
  { id: "phone", header: "WhatsApp", size: 120, cell: ({ row }) => cellText(row.original.phone) },
  { id: "job", header: "Cargo", size: 130, cell: ({ row }) => cellText(row.original.job) },
  { id: "corporate_email", header: "E-mail corporativo", size: 180, cell: ({ row }) => cellText(row.original.corporate_email) },
  { id: "erp_code", header: "Código ERP", size: 110, cell: ({ row }) => cellText(row.original.erp_code) },
];

const RESULT_COLUMNS = [{ id: "email", header: "E-mail", size: 240, cell: ({ row }) => row.original.email || "-" }];

/**
 * Importação de colaboradores por planilha em três passos: planilha, prévia e
 * resultado. Só monta aberto. `onClose(imported)` diz se alguém entrou, para a
 * lista recarregar.
 */
export default function ImportEmployeesModal({ onClose }) {
  const authService = useService("auth");
  const [step, setStep] = useState(STEP_UPLOAD);
  const [rows, setRows] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Planilha recusada: a área de soltar volta vazia (sem o nome com o ✓ verde)
  const [dropKey, setDropKey] = useState(0);

  const validRows = rows.filter((_, i) => !rowErrors[i]);
  const errorCount = Object.keys(rowErrors).length;
  const importedCount = (importResult?.imported || 0) + (importResult?.linked || 0);

  function close() {
    if (loading) return;
    onClose(importedCount > 0);
  }

  function rejectSheet(message) {
    toast.error(message);
    setDropKey((key) => key + 1);
  }

  async function handleFile(file) {
    let json;
    try {
      json = (await readSheetFile(file)).map(normalizeEmployeeSheetRow);
    } catch (err) {
      console.error(err);
      rejectSheet("Não foi possível ler a planilha");
      return;
    }

    if (json.length === 0) {
      rejectSheet("A planilha está vazia");
      return;
    }

    const headers = new Set(json.flatMap((row) => Object.keys(row)));
    const missingCols = REQUIRED_COLS.filter((col) => !headers.has(col));
    if (missingCols.length > 0) {
      rejectSheet(`Colunas obrigatórias ausentes: ${labels(missingCols)}`);
      return;
    }

    if (json.length > MAX_ROWS) {
      rejectSheet(`Limite de ${MAX_ROWS} colaboradores por importação`);
      return;
    }

    setRows(json);
    setRowErrors(validateRows(json));
    setStep(STEP_PREVIEW);
  }

  function resetState() {
    setStep(STEP_UPLOAD);
    setRows([]);
    setRowErrors({});
    setImportResult(null);
  }

  async function upload() {
    setLoading(true);
    try {
      // Mesmo payload da importação antiga
      const dataToSend = validRows.map((item) => ({
        name: (item.name || "").toString().trim(),
        email: (item.email || "").toString().trim().toLowerCase(),
        document: (item.document || "").toString().replace(/\D/g, ""),
        phone: item.phone ? (item.phone || "").toString().replace(/\D/g, "") : null,
        job: item.job ? item.job.toString().trim() : null,
        corporate_email: item.corporate_email ? item.corporate_email.toString().trim().toLowerCase() : null,
        erp_code: item.erp_code ? item.erp_code.toString().trim() : null,
        role: "EMP",
      }));

      // No erro, o service já mostra o toast (e o `data` dele é o erro do axios)
      const res = await authService.batchAddUser(dataToSend);
      if (res && !res.error && res.data) {
        setImportResult(res.data);
        setStep(STEP_RESULT);
      } else if (!res?.error) {
        toast.error("Erro ao importar colaboradores");
      }
    } catch (err) {
      toast.error("Erro ao importar colaboradores");
    }
    setLoading(false);
  }

  let content;
  let footer;

  if (step === STEP_RESULT && importResult) {
    content = (
      <Body>
        <ImportCounts
          items={[
            { count: importedCount, one: "importado", many: "importados" },
            { count: importResult.skipped, one: "ignorado", many: "ignorados" },
            { count: importResult.failed, one: "com falha", many: "com falha" },
          ]}
        />
        {importResult.details?.length > 0 ? <ImportResultTable details={importResult.details} columns={RESULT_COLUMNS} /> : null}
      </Body>
    );
    footer = <Button $text="Fechar" $variant="primary" $action={close} $noWidth />;
  } else if (step === STEP_PREVIEW) {
    // Status logo depois do #: as outras colunas passam da largura do modal e rolam
    const [indexColumn, ...dataColumns] = PREVIEW_COLUMNS;
    const columns = [
      indexColumn,
      { id: "status", header: "Status", size: 240, cell: ({ row }) => <PreviewStatusCell errors={rowErrors[row.index]} /> },
      ...dataColumns,
    ];
    const importLabel = `Importar ${validRows.length} ${validRows.length === 1 ? "colaborador" : "colaboradores"}`;

    content = (
      <Body>
        <ImportCounts
          items={[
            { count: rows.length, one: "linha", many: "linhas" },
            { count: validRows.length, one: "válida", many: "válidas" },
            { count: errorCount, one: "com erro", many: "com erro", hideZero: true },
          ]}
        />
        {errorCount > 0 ? <Notice $variant="red" $text="As linhas com erro não serão importadas." /> : null}
        <TableScroll>
          <BasicTable data={rows} columns={columns} getRowId={(_, index) => String(index)} />
        </TableScroll>
      </Body>
    );
    footer = (
      <>
        <Button $text="Escolher outra planilha" $variant="outline" $action={resetState} $isDisabled={loading} $noWidth />
        <Button
          $text={importLabel}
          $variant="primary"
          $action={upload}
          $isLoading={loading}
          $isDisabled={validRows.length === 0}
          $noWidth
        />
      </>
    );
  } else {
    content = (
      <Body>
        <Stack>
          <Text
            $text="Campos obrigatórios: <strong>%s</strong>"
            $replaces={[labels(REQUIRED_COLS)]}
            $variant="paragraph_small_medium"
            $color="N700"
            $colorEmphasis="N900"
          />
          <Text $text="Campos opcionais: %s" $replaces={[labels(OPTIONAL_COLS)]} $variant="paragraph_small_medium" $color="N700" />
          <Text $text={`Até ${MAX_ROWS} colaboradores por planilha.`} $variant="caption_medium" $color="N400" />
        </Stack>
        <Inline>
          <ButtonLink $text="Baixar modelo de planilha" $action={downloadTemplate} $noPadding $noWidth />
        </Inline>
        <SheetDropZone key={dropKey} onFile={handleFile} />
      </Body>
    );
    footer = <Button $text="Cancelar" $variant="outline" $action={close} $noWidth />;
  }

  return (
    <ModalDefault
      $showModal
      $setShowModal={close}
      $noClose={loading}
      $size="large"
      $title="Importar colaboradores"
      $contentBackground="N0"
      $footer={footer}
    >
      {content}
    </ModalDefault>
  );
}
